import UploadModal, { ErrorableFileState } from "components/UploadModal/UploadModal";
import { useState } from "react";
import {
	AutocadUploadContainer,
	AutocadUploadContent,
	AutocadUploadSubheader,
	AutocadUploadText,
	AutocadUploadTextBold
} from "./AutocadUpload.styles";
import {
	AttachCustomQuoteText,
	AttachFile,
	CustomQuoteDwgInstructionsText,
	CustomQuoteDwgText,
	CustomQuoteFileTypeIncorrectErrorText,
	DwgSupportedFiles,
	FileDuplicateText,
	FileTooLargeText,
	FileTooSmallText,
	RequiredFieldsText,
	SupportedFilesHeader
} from "constants/text";
import { RequiredFieldWrapper } from "pages/CustomQuote/AddQuotePage/addQuotePage.styles";
import { Box } from "@mui/material";
import UploadFile from "@mui/icons-material/UploadFile";
import { AttachedFilesContainer, StyledUploadButton } from "components/SketchUpload/SketchUpload.styles";
import UploadView from "components/UploadView/UploadView";
import { ReplacementFile } from "features/reducers/replacementOrder/cart";

interface AutocadUploadProps {
	onFilesSelected?: (files: ReplacementFile[]) => void;
}

export const AutocadUpload = ({ onFilesSelected }: AutocadUploadProps) => {
	const [open, setOpen] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<ReplacementFile[]>([]);

	const dummyItem = { id: "custom-quote-upload" } as any;

	const handleClose = (files: ErrorableFileState[]) => {
		const validFiles = files.map(({ file }, index) => {
			return {
				name: file.name,
				size: file.size,
				id: `${file}-${index}`,
				fileUrl: URL.createObjectURL(file),
				originalLineItemId: ""
			} as ReplacementFile;
		});
		const oldSelectedFiles = uploadedFiles;
		setUploadedFiles((prev) => [...prev, ...validFiles]);
		onFilesSelected?.([...oldSelectedFiles, ...validFiles]);
		setOpen(false);
	};

	const removeFile = (index: number) => {
		const newFiles = uploadedFiles.filter((_, i) => i !== index);
		setUploadedFiles(newFiles);
		onFilesSelected?.(newFiles);
	};

	const isValidCqUpload = (file: File, uploadedFiles: ReplacementFile[]): boolean => {
		const isValid = isValidDwgFile(file) || file.type === "application/pdf";
		const isDuplicate = uploadedFiles.some((uploadedFile) => uploadedFile.name === file.name);
		return isValid && !isDuplicate && file.size <= 10 * 1024 * 1024;
	};

	const isValidDwgFile = (file: File): boolean => {
		const validExtension = file.name.toLowerCase().endsWith(".dwg");
		const validMimeType = file.type === "image/vnd.dwg";
		return validExtension || validMimeType;
	};

	const processInvalidFileStates = (
		file: File,
		maxMB: number,
		fileUploads: ReplacementFile[] = []
	): string | undefined => {
		if (
			fileUploads.length > 0 &&
			fileUploads.find((selectedFile: ReplacementFile) => selectedFile.name === file.name)
		) {
			return FileDuplicateText;
		}

		if (file.size > maxMB * (1024 * 1024)) {
			return FileTooLargeText;
		}

		if (!isValidDwgFile(file) && !(file.type === "application/pdf")) {
			return CustomQuoteFileTypeIncorrectErrorText;
		}

		if (file.size === 0) {
			return FileTooSmallText;
		}

		return undefined;
	};

	return (
		<AutocadUploadContainer>
			<h3>{AttachCustomQuoteText}</h3>
			<AutocadUploadContent>
				<RequiredFieldWrapper>{RequiredFieldsText}</RequiredFieldWrapper>
				<AutocadUploadText>{CustomQuoteDwgText}</AutocadUploadText>
				<AutocadUploadSubheader>{CustomQuoteDwgInstructionsText}</AutocadUploadSubheader>
				{(!uploadedFiles.length || uploadedFiles.length < 2) && (
					<Box>
						<StyledUploadButton
							variant="outlined"
							onClick={() => setOpen(true)}
							disabled={uploadedFiles.length === 2}
							startIcon={<UploadFile />}
						>
							{AttachFile}
						</StyledUploadButton>
						<AutocadUploadSubheader>
							{SupportedFilesHeader}&nbsp;
							<AutocadUploadTextBold>{DwgSupportedFiles}</AutocadUploadTextBold>
						</AutocadUploadSubheader>
					</Box>
				)}
			</AutocadUploadContent>

			{uploadedFiles.length > 0 && (
				<AttachedFilesContainer>
					{uploadedFiles.map((file, index) => (
						<UploadView
							key={`${file.name}-${index}`}
							index={index}
							file={{
								name: file.name,
								size: file.size,
								fileUrl: file.fileUrl,
								id: `file-${index}`,
								originalLineItemId: ""
							}}
							onImageRemove={() => removeFile(index)}
						/>
					))}
				</AttachedFilesContainer>
			)}

			<UploadModal
				handleClose={handleClose}
				item={dummyItem}
				open={open}
				maxFiles={2}
				fileUploads={[]}
				fileSelectionText={CustomQuoteDwgInstructionsText}
				dragDropAcceptedFileTypes=".dwg,.pdf"
				defaultSupportedFilesText={DwgSupportedFiles}
				checkIsValidFile={isValidCqUpload}
				supportedFileTypes={["application/octet-stream", "application/pdf"]}
				handleUploadErrorStates={processInvalidFileStates}
			/>
		</AutocadUploadContainer>
	);
};
